"use client";

import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Box, Button, Link, Typography } from "@mui/material";
import type { SponsorCampaign } from "../campaignTypes";
import { draftKingsCampaign } from "../campaigns/draft-kings";
import { PORTAL_MAX_WIDTH, portalFont } from "../partnerTheme";
import SponsorResultsPage from "./SponsorResultsPage";
import UnitedBrandOverview from "./UnitedBrandOverview";

const ACCENT = "#1F7A3A";
const LINE = "rgba(255,255,255,0.12)";
const MUTED = "rgba(244,245,247,0.68)";

type TabId = "about" | "digital" | "calendar" | "cases" | "q4";

const TABS: { id: TabId; label: string }[] = [
  { id: "about", label: "ComedyUO" },
  { id: "digital", label: "Digital" },
  { id: "calendar", label: "Calendar" },
  { id: "cases", label: "Case Studies" },
  { id: "q4", label: "Q4 Taping" },
];

const KIEHLS = {
  francis: "https://storage.googleapis.com/cuo-website/Screenshot%202026-10-28%20at%204.57.35%E2%80%AFPM.png",
  gabby: "https://storage.googleapis.com/cuo-website/Screenshot%202026-10-28%20at%204.58.13%E2%80%AFPM.png",
  sydnee: "https://storage.googleapis.com/cuo-website/Screenshot%202026-10-28%20at%204.57.54%E2%80%AFPM.png",
  bags: "https://storage.googleapis.com/cuo-website/Screenshot%202026-10-28%20at%203.29.24%E2%80%AFPM.png",
  bar: "https://storage.googleapis.com/cuo-website/Screenshot%202026-10-28%20at%203.29.12%E2%80%AFPM.png",
  shelves: "https://storage.googleapis.com/cuo-website/Screenshot%202026-10-28%20at%203.28.55%E2%80%AFPM.png",
  backdrop: "https://storage.googleapis.com/cuo-website/Screenshot%202026-10-28%20at%203.28.34%E2%80%AFPM.png",
  stage: "https://storage.googleapis.com/cuo-website/Screenshot%202026-10-28%20at%207.34.52%E2%80%AFPM.png",
  guests: "https://storage.googleapis.com/cuo-website/Screenshot%202026-10-28%20at%207.34.29%E2%80%AFPM.png",
  service: "https://storage.googleapis.com/cuo-website/Screenshot%202026-10-28%20at%207.34.03%E2%80%AFPM.png",
  group: "https://storage.googleapis.com/cuo-website/Screenshot%202026-10-28%20at%207.33.12%E2%80%AFPM.png",
} as const;

const ARIZONA = {
  room: "https://storage.googleapis.com/cuo-website/Screenshot%202026-10-28%20at%203.16.29%E2%80%AFPM.png",
  cans: "https://storage.googleapis.com/cuo-website/Screenshot%202026-10-28%20at%203.16.49%E2%80%AFPM.png",
  wall: "https://storage.googleapis.com/cuo-website/Screenshot%202026-10-28%20at%203.17.07%E2%80%AFPM.png",
  phil: "https://storage.googleapis.com/cuo-website/Screenshot%202026-10-28%20at%203.19.42%E2%80%AFPM.png",
  usama: "https://storage.googleapis.com/cuo-website/Screenshot%202026-10-28%20at%203.19.50%E2%80%AFPM.png",
  ricky: "https://storage.googleapis.com/cuo-website/Screenshot%202026-10-28%20at%203.19.59%E2%80%AFPM.png",
  shirts: "https://storage.googleapis.com/cuo-website/Screenshot%202026-10-28%20at%203.20.26%E2%80%AFPM.png",
  seconds: "https://storage.googleapis.com/cuo-website/Screenshot%202026-10-28%20at%203.20.58%E2%80%AFPM.png",
  dollar: "https://storage.googleapis.com/cuo-website/Screenshot%202026-10-28%20at%203.21.13%E2%80%AFPM.png",
  reshop: "https://storage.googleapis.com/cuo-website/Screenshot%202026-10-28%20at%203.21.29%E2%80%AFPM.png",
  bestOf: "https://storage.googleapis.com/cuo-website/Screenshot%202026-10-28%20at%203.22.24%E2%80%AFPM.png",
  timeout: "https://storage.googleapis.com/cuo-website/Screenshot%202026-10-28%20at%203.22.08%E2%80%AFPM.png",
  stage: "https://storage.googleapis.com/cuo-website/Screenshot%202026-10-28%20at%208.00.32%E2%80%AFPM.png",
  crowd: "https://storage.googleapis.com/cuo-website/Screenshot%202026-10-28%20at%208.01.16%E2%80%AFPM.png",
  deck: "https://storage.googleapis.com/cuo-website/Screenshot%202026-10-28%20at%208.01.56%E2%80%AFPM.png",
} as const;

const SHOWS: {
  id: string;
  name: string;
  date: string;
  guests: string;
  description: string;
  image: string;
  imageAlt: string;
  sponsorship?: "food" | "residency";
  moreHref?: string;
  moreLabel?: string;
}[] = [
  {
    id: "food-tour",
    name: "Food Tour",
    date: "January 11–15 and January 20–22, 2027",
    guests: "1,000 guests",
    description: "Eight New York restaurants over two weeks. A different room and lineup each night.",
    image: "https://storage.googleapis.com/cuo-website/Chris%20Redd%20FT.png",
    imageAlt: "Chris Redd at a ComedyUO Food Tour show",
    sponsorship: "food",
    moreHref: "/partners/gti-food-tour",
    moreLabel: "Explore more about the Food Tour",
  },
  {
    id: "residency",
    name: "The Residency",
    date: "February 4, 11, 18, and 25, 2027",
    guests: "About 1,100 guests across the four nights",
    description: "Four nights at the Empire State Building, with hospitality and an observatory tour.",
    image: "https://storage.googleapis.com/cuo-website/ESB%20Tickets.jpg",
    imageAlt: "Empire State Building comedy show tickets",
    sponsorship: "residency",
    moreHref: "/partners/gti-residency",
    moreLabel: "Explore more about the Residency",
  },
  {
    id: "wilson",
    name: "St. Patrick's Day at the Wilson Store",
    date: "March 17, 2027",
    guests: "250 guests",
    description: "A holiday night inside the Wilson store.",
    image: "https://storage.googleapis.com/cuo-website/Chris+Distefano+at+Wilson.jpg",
    imageAlt: "Chris Distefano performing at Wilson",
  },
  {
    id: "paragon",
    name: "Paragon Sports",
    date: "April 1–May 29, 2027",
    guests: "1,800 guests",
    description: "We activate 3 floors for simultaneous shows across 3 days.",
    image: "https://storage.googleapis.com/cuo-website/Marcello+Hernandez+at+Paragon.jpg",
    imageAlt: "Marcello Hernández performing at Paragon Sports",
  },
  {
    id: "stadium",
    name: "Stadium Tour",
    date: "April through July 2027",
    guests: "Rooms of about 75 to 200",
    description: "The summer activation. Intimate shows in stadium spaces fans do not usually enter.",
    image: "/images/partners/amex-esb/stadiums/wrigley.jpg",
    imageAlt: "A stadium, the setting for the summer tour",
  },
  {
    id: "zabars",
    name: "Zabar's Mother's Day",
    date: "May 9, 2027",
    guests: "120 guests",
    description: "Mother's Day at Zabar's.",
    image: "https://storage.googleapis.com/cuo-website/Dave+Attell+at+Zabar's.jpg",
    imageAlt: "Dave Attell at Zabar's",
  },
  {
    id: "luger",
    name: "Peter Luger's",
    date: "October 14, 2027",
    guests: "120 guests",
    description: "The annual steakhouse night.",
    image: "https://storage.googleapis.com/cuo-website/comedy+in+nyc.jpg",
    imageAlt: "Comedy at Peter Luger",
  },
  {
    id: "katz",
    name: "Katz's",
    date: "November 12, 2027",
    guests: "260 guests",
    description: "The deli show.",
    image: "https://storage.googleapis.com/cuo-website/Katz%27s+Deli.jpg",
    imageAlt: "Katz's Delicatessen",
  },
  {
    id: "pajama",
    name: "Pajama Comedy",
    date: "December 12, 2027",
    guests: "140 guests",
    description: "A themed night to close the year. The venue is still being set.",
    image: "https://storage.googleapis.com/cuo-website/UO_071324-080.jpg",
    imageAlt: "A ComedyUO audience",
  },
];

const FOOD_SUPPORTER_GET = [
  "Product featured in each venue",
  "Your logo included on the promotional flyer for the tour",
  "5 dedicated story posts on Instagram",
  "Logo included in an email blast to our 39k+ waiting list",
  "10 tickets to the tour",
  "Reserved seating for your team",
  "Logo featured in the recap video",
];

const FOOD_SPONSOR_GET = [
  "Promo video with a product feature",
  "Product feature in the green room",
  "Product tie-ins with the kitchens and serving",
  "Branded moment with packaging leftovers",
  "Opportunity to distribute giveaways",
  "Your branding featured in all promotional materials",
  "Dedicated shout-out at every show",
  "Dedicated grid post on Instagram and TikTok",
  "10 dedicated story posts on Instagram",
  "Feature on an email blast to our 39k+ waiting list",
  "Organic inclusion on influencers' posts",
  "Category exclusivity. No competing brand can sponsor the tour.",
  "Discounted sponsor fee for a future show",
  "20 tickets to the tour",
  "Reserved seating for your team",
  "Feature in the recap video",
];

const RESIDENCY_SUPPORTER_GET = [
  "Your product highlighted on-site",
  "Small, countertop signage throughout the venue",
  "2 tickets to the event",
];

const RESIDENCY_SPONSOR_GET = [
  "Large, freestanding signage and promotional materials throughout the venue",
  "Shout-out during pre-show remarks",
  "Your logo included in promotional materials",
  "Dedicated story posts on Instagram",
  "Inclusion on an email blast to our 39k+ waitlist",
  "Organic inclusion on influencers' posts",
  "4 total tickets to the event",
  "Recap videos posted after the event with the sponsor",
  "Tickets allocated for select customers",
];

const RESIDENCY_PARTNER_GET = [
  "On-stage branding, except taped sets",
  "Brand inclusion on the commemorative ticket",
  "Inclusion in lead-up promotional content",
  "Your logo featured in all promotional materials",
  "Dedicated promo video",
  "Feature on an email blast to our 39k+ waitlist",
  "Category exclusivity. If the focus product is in tech, no other tech firm is promoted.",
  "No sponsor fee for a future event of 100+ guests",
  "8 total tickets to the event",
  "4 tickets to the next show of your choice, including Katz's Deli",
];

const TAB_IDS: TabId[] = ["about", "digital", "calendar", "cases", "q4"];

export default function GtiPortalPage({ campaign }: { campaign: SponsorCampaign }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const requested = searchParams.get("tab");
  const [tab, setTab] = useState<TabId>(TAB_IDS.includes(requested as TabId) ? (requested as TabId) : "about");

  function openTab(next: TabId) {
    setTab(next);
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("tab", next);
    setSearchParams(nextParams, { replace: true });
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  return (
    <Box sx={{ bgcolor: "#000", color: "#fff", minHeight: "100vh", fontFamily: portalFont }}>
      <Box
        component="header"
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 40,
          bgcolor: "rgba(0,0,0,0.78)",
          backdropFilter: "blur(14px)",
          borderBottom: `1px solid ${LINE}`,
        }}
      >
        <Box
          sx={{
            maxWidth: PORTAL_MAX_WIDTH,
            mx: "auto",
            px: { xs: 2, md: 4 },
            py: 1.25,
            display: "flex",
            alignItems: "center",
            gap: { xs: 1.5, md: 2.5 },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, flexShrink: 0 }}>
            <Box
              component="img"
              src="https://storage.googleapis.com/cuo-website/Screenshot%202026-10-28%20at%203.57.27%E2%80%AFPM.png"
              alt="Green Thumb"
              sx={{ height: { xs: 28, md: 36 }, width: "auto", display: "block" }}
            />
            <Box sx={{ width: "1px", height: 28, bgcolor: "rgba(255,255,255,0.35)" }} />
            <Box
              component="img"
              src={campaign.comedyuoLogo}
              alt="ComedyUO"
              sx={{ height: { xs: 36, md: 48 }, width: "auto" }}
            />
          </Box>
          <Box sx={{ display: "flex", gap: 0.5, overflowX: "auto", flex: 1, minWidth: 0 }}>
            {TABS.map((item) => {
              const active = tab === item.id;
              return (
                <Button
                  key={item.id}
                  onClick={() => openTab(item.id)}
                  sx={{
                    fontFamily: portalFont,
                    fontWeight: 650,
                    textTransform: "none",
                    fontSize: { xs: "0.78rem", md: "0.85rem" },
                    color: active ? "#0a0a0a" : MUTED,
                    bgcolor: active ? "#fff" : "transparent",
                    borderRadius: "999px",
                    px: { xs: 1.4, md: 1.8 },
                    py: 0.7,
                    whiteSpace: "nowrap",
                    flexShrink: 0,
                    "&:hover": { bgcolor: active ? "#fff" : "rgba(255,255,255,0.06)", color: active ? "#0a0a0a" : "#fff" },
                  }}
                >
                  {item.label}
                </Button>
              );
            })}
          </Box>
        </Box>
      </Box>

      {tab === "about" && (
        <UnitedBrandOverview
          onContinueToResidency={() => openTab("digital")}
          eyebrow="Prepared for Green Thumb Industries"
          intro="Since April 2021 we have hosted 350+ shows with 250+ comics. This is who we are. The next tab is the digital offering."
          skipCta="Open the digital offering"
          bridgeTitle="The digital offering is next."
          bridgeBody="The taping is our flagship content package. The next tab is a real season we already produced."
          bridgeCta="Open the digital offering"
          bridgeImage="https://storage.googleapis.com/cuo-website/Great%20Jones%20Squatch.jpg"
          bridgeImageAlt="Dr. Squatch at a ComedyUO taping"
        />
      )}
      {tab === "digital" && <DigitalTab />}
      {tab === "calendar" && <CalendarTab />}
      {tab === "cases" && <CaseStudiesTab />}
      {tab === "q4" && <Q4Tab />}

      <Box sx={{ borderTop: `1px solid ${LINE}`, py: 4, px: 3, textAlign: "center" }}>
        <Typography sx={{ fontFamily: portalFont, color: MUTED, fontSize: "0.95rem" }}>
          Mike Zbyszewski · Sales & Partnerships ·{" "}
          <Link href="mailto:mike@comedyuo.com" sx={{ color: "#fff" }}>
            mike@comedyuo.com
          </Link>
        </Typography>
      </Box>
    </Box>
  );
}

function DigitalTab() {
  return (
    <Box>
      <SponsorResultsPage
        campaign={draftKingsCampaign}
        embedded
        heroCopy={{
          headline: "The taping.",
          summary:
            "A taping is where we license real comic material. The season below is the one we produced with Dr. Squatch.",
          points: [
            "Shoot a 10-minute taped set",
            "7–10 comics",
            "Produce the full sets and distribute them",
            "Cut 18–34 clips and distribute them over 3 months",
          ],
        }}
      />
    </Box>
  );
}

function CalendarTab() {
  const [openId, setOpenId] = useState<string | null>(null);

  return (
    <Box sx={{ maxWidth: PORTAL_MAX_WIDTH, mx: "auto", px: { xs: 2.5, md: 4 }, py: { xs: 4, md: 6 } }}>
      <Typography sx={{ fontFamily: portalFont, color: ACCENT, fontWeight: 700, letterSpacing: "0.14em", fontSize: "0.75rem", mb: 1 }}>
        2027
      </Typography>
      <Typography sx={{ fontFamily: portalFont, fontWeight: 750, fontSize: { xs: "2rem", md: "3rem" }, letterSpacing: "-0.03em", mb: 3, color: "#fff" }}>
        Tentpole calendar.
      </Typography>
      <Box sx={{ display: "grid", gap: 2 }}>
        {SHOWS.map((show) => {
          const open = openId === show.id;
          return (
            <Box key={show.id} sx={{ border: `1px solid ${LINE}`, borderRadius: "16px", overflow: "hidden", bgcolor: "rgba(255,255,255,0.03)" }}>
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "300px 1fr" } }}>
                <Box component="img" src={show.image} alt={show.imageAlt} sx={{ width: "100%", height: { xs: 220, md: 260 }, objectFit: "cover" }} />
                <Box sx={{ p: { xs: 2.5, md: 3.5 }, display: "flex", flexDirection: "column" }}>
                  <Typography sx={{ fontFamily: portalFont, color: "#fff", fontWeight: 750, fontSize: "1.45rem", mb: 0.75 }}>{show.name}</Typography>
                  <Typography sx={{ fontFamily: portalFont, color: "#fff", mb: 0.4 }}>{show.date}</Typography>
                  <Typography sx={{ fontFamily: portalFont, color: MUTED, mb: 1.5 }}>{show.guests}</Typography>
                  <Typography sx={{ fontFamily: portalFont, color: "#fff", lineHeight: 1.55, mb: show.sponsorship || show.moreHref ? 2.5 : 0 }}>{show.description}</Typography>
                  {show.sponsorship || show.moreHref ? (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.25, mt: "auto" }}>
                      {show.moreHref ? (
                        <Button
                          href={show.moreHref}
                          sx={{
                            fontFamily: portalFont,
                            fontWeight: 700,
                            textTransform: "none",
                            color: "#fff",
                            border: "1px solid rgba(255,255,255,0.4)",
                            borderRadius: "999px",
                            px: 2,
                            py: 0.7,
                            "&:hover": { bgcolor: "rgba(255,255,255,0.08)", borderColor: "#fff" },
                          }}
                        >
                          {show.moreLabel}
                        </Button>
                      ) : null}
                      {show.sponsorship ? (
                        <Button
                          onClick={() => setOpenId(open ? null : show.id)}
                          sx={{
                            fontFamily: portalFont,
                            fontWeight: 700,
                            textTransform: "none",
                            color: "#0a0a0a",
                            bgcolor: "#fff",
                            borderRadius: "999px",
                            px: 2,
                            py: 0.7,
                            "&:hover": { bgcolor: "rgba(255,255,255,0.88)" },
                          }}
                        >
                          {open ? "Hide sponsorship tiers" : "View sponsorship tiers"}
                        </Button>
                      ) : null}
                    </Box>
                  ) : null}
                </Box>
              </Box>
              {open && show.sponsorship === "food" ? <FoodTiers /> : null}
              {open && show.sponsorship === "residency" ? <ResidencyTiers /> : null}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

function FoodTiers() {
  return (
    <Box sx={{ px: { xs: 2.5, md: 4 }, pt: { xs: 3.5, md: 4.5 }, pb: { xs: 3.5, md: 4.5 }, borderTop: `1px solid ${LINE}` }}>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2.5, alignItems: "end" }}>
        <TierCard name="Supporter" price="$10,000" gets={FOOD_SUPPORTER_GET} />
        <TierCard name="Sponsor" price="$20,000" gets={FOOD_SPONSOR_GET} />
      </Box>
    </Box>
  );
}

function ResidencyTiers() {
  return (
    <Box sx={{ px: { xs: 2.5, md: 4 }, pt: { xs: 3.5, md: 4.5 }, pb: { xs: 3.5, md: 4.5 }, borderTop: `1px solid ${LINE}` }}>
      <Typography sx={{ fontFamily: portalFont, color: "#fff", mb: 3, lineHeight: 1.55, maxWidth: 760 }}>
        $25,000 Supporter. $50,000 is the lifestyle sponsor: the brand in the room, on the stories, and in the email. $75,000 is title for all four shows.
      </Typography>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" }, gap: 2.5, alignItems: "end" }}>
        <TierCard name="Supporter" price="$25,000" gets={RESIDENCY_SUPPORTER_GET} badge="All 4 shows" />
        <TierCard name="Lifestyle sponsor" price="$50,000" gets={RESIDENCY_SPONSOR_GET} plus="Supporter benefits, plus:" badge="All 4 shows" />
        <TierCard name="Title" price="$75,000" gets={RESIDENCY_PARTNER_GET} plus="Lifestyle sponsor benefits, plus:" badge="All 4 shows" />
      </Box>
    </Box>
  );
}

function CaseStudiesTab() {
  return (
    <Box sx={{ maxWidth: PORTAL_MAX_WIDTH, mx: "auto", px: { xs: 2.5, md: 4 }, py: { xs: 4, md: 6 }, color: "#fff" }}>
      <Typography sx={{ fontFamily: portalFont, color: ACCENT, fontWeight: 700, letterSpacing: "0.14em", fontSize: "0.75rem", mb: 1 }}>
        CASE STUDIES
      </Typography>
      <Typography sx={{ fontFamily: portalFont, fontWeight: 750, fontSize: { xs: "2rem", md: "3rem" }, letterSpacing: "-0.03em", color: "#fff", mb: { xs: 4, md: 5 } }}>
        Two branded case studies.
      </Typography>
      <Box sx={{ display: "grid", gap: { xs: 3, md: 4 } }}>
        <ArizonaSlide />
        <KiehlsSlide />
      </Box>
      <BrandedExamples />
    </Box>
  );
}

function ArizonaSlide() {
  return (
    <Box sx={{ border: `1px solid ${LINE}`, borderRadius: "20px", p: { xs: 2.5, md: 4 }, bgcolor: "rgba(255,255,255,0.03)" }}>
      <Typography sx={{ fontFamily: portalFont, color: ACCENT, fontWeight: 700, letterSpacing: "0.14em", fontSize: "0.75rem", mb: 1 }}>
        01
      </Typography>
      <Typography sx={{ fontFamily: portalFont, color: "#fff", fontWeight: 750, fontSize: { xs: "2.4rem", md: "3.25rem" }, letterSpacing: "-0.03em", lineHeight: 1.05, mb: 1.5 }}>
        AriZona
      </Typography>
      <Typography sx={{ fontFamily: portalFont, color: "#fff", fontSize: { xs: "1.15rem", md: "1.3rem" }, lineHeight: 1.5, maxWidth: 760, mb: 4 }}>
        Branded content and a public show.
      </Typography>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 1.5, mb: 4 }}>
        <Box sx={{ border: `1px solid ${LINE}`, borderRadius: "16px", px: 2.25, py: 2, bgcolor: "#000" }}>
          <Typography sx={{ fontFamily: portalFont, color: ACCENT, fontWeight: 700, fontSize: "0.95rem", mb: 1 }}>
            What AriZona wanted
          </Typography>
          <Typography sx={{ fontFamily: portalFont, color: "#fff", fontWeight: 650, fontSize: { xs: "1.15rem", md: "1.3rem" }, letterSpacing: "-0.02em", lineHeight: 1.35 }}>
            A 99 cent show and a campaign.
          </Typography>
        </Box>
        <Box sx={{ border: `1px solid ${LINE}`, borderRadius: "16px", px: 2.25, py: 2, bgcolor: "#000" }}>
          <Typography sx={{ fontFamily: portalFont, color: ACCENT, fontWeight: 700, fontSize: "0.95rem", mb: 1 }}>
            What we delivered
          </Typography>
          <Typography sx={{ fontFamily: portalFont, color: "#fff", fontSize: "1.05rem", lineHeight: 1.55 }}>
            A public show at ReShop with Phil Hanley, Usama Siddique, and Ricky Velez. Tickets were $.99, and we refunded the .01. The room was built for the brand: set design, product on site, and signage. The campaign was three pieces of branded content made for the show.
          </Typography>
        </Box>
      </Box>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(3, minmax(0, 1fr))", md: "repeat(3, 140px)" }, gap: { xs: 2, md: 3 }, justifyContent: "start", mb: 4 }}>
        <Talent name="Phil Hanley" src={ARIZONA.phil} />
        <Talent name="Usama Siddique" src={ARIZONA.usama} />
        <Talent name="Ricky Velez" src={ARIZONA.ricky} />
      </Box>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", md: "1fr 1fr 1fr 1fr" }, gap: 1.25, mb: 5 }}>
        {[
          { src: ARIZONA.room, alt: "The AriZona show at ReShop" },
          { src: ARIZONA.stage, alt: "A comic performing in front of the AriZona shirt wall" },
          { src: ARIZONA.crowd, alt: "The crowd at the AriZona show" },
          { src: ARIZONA.wall, alt: "The AriZona shirt wall and set" },
          { src: ARIZONA.cans, alt: "AriZona product at the show" },
          { src: ARIZONA.deck, alt: "A custom AriZona skate deck" },
          { src: ARIZONA.shirts, alt: "Custom AriZona show shirts" },
        ].map((photo) => (
          <Box key={photo.src} component="img" src={photo.src} alt={photo.alt} sx={{ width: "100%", height: { xs: 160, md: 210 }, objectFit: "cover", borderRadius: "12px", display: "block" }} />
        ))}
      </Box>
      <Box sx={{ display: "flex", flexDirection: { xs: "column", lg: "row" }, gap: { xs: 4, lg: 5 }, alignItems: "flex-start" }}>
        <Box>
          <Typography sx={{ fontFamily: portalFont, color: "#fff", fontWeight: 750, fontSize: { xs: "1.35rem", md: "1.6rem" }, letterSpacing: "-0.02em", mb: 2 }}>
            The branded content.
          </Typography>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(3, minmax(0, 190px))" }, gap: 1.5, justifyContent: "start", alignItems: "start" }}>
            <Challenge
              title="99 Second Challenge"
              body="Participants got 99 seconds to find custom AriZona merch."
              src={ARIZONA.seconds}
              alt="A guest finding AriZona merch on the clock"
            />
            <Challenge
              title="Spend under $1"
              body="Find something that costs under $1, other than AriZona."
              src={ARIZONA.dollar}
              alt="Guests holding a dollar bill in New York"
            />
            <Challenge
              title="The $.99 ticket"
              body="Tickets were $.99, and we refunded the .01."
              src={ARIZONA.reshop}
              alt="The ReShop story for the 99 cent AriZona show"
            />
          </Box>
        </Box>
        <Box sx={{ width: { xs: "100%", lg: 360 }, flexShrink: 0 }}>
          <Typography sx={{ fontFamily: portalFont, color: "#fff", fontWeight: 750, fontSize: { xs: "1.35rem", md: "1.6rem" }, letterSpacing: "-0.02em", mb: 1.25 }}>
            The organic press.
          </Typography>
          <Typography sx={{ fontFamily: portalFont, color: "#fff", fontSize: "1.05rem", lineHeight: 1.55, mb: 2 }}>
            Time Out covered the 99 cent show on its own.
          </Typography>
          <Box sx={{ display: "grid", gap: 2 }}>
            <Box component="img" src={ARIZONA.timeout} alt="Time Out on the 99 cent AriZona show" sx={{ width: "100%", height: "auto", display: "block", bgcolor: "#fff", borderRadius: "12px" }} />
            <Box component="img" src={ARIZONA.bestOf} alt="Time Out Best of the City" sx={{ width: "100%", height: "auto", display: "block", bgcolor: "#fff", borderRadius: "12px" }} />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

function KiehlsSlide() {
  const goals = [
    ["01", "Activate their flagship location."],
    ["02", "Handpick comics for the setting."],
    ["03", "Create on-site promo opportunities."],
    ["04", "Build in moments creators share."],
  ];
  const photos: { src: string; alt: string }[] = [
    { src: KIEHLS.stage, alt: "A comic performing in front of the Kiehl's product wall" },
    { src: KIEHLS.guests, alt: "Guests together inside the Kiehl's store" },
    { src: KIEHLS.group, alt: "Influencers in front of the Kiehl's and ComedyUO step and repeat" },
    { src: KIEHLS.service, alt: "Katz's serving inside the Kiehl's store" },
    { src: KIEHLS.bags, alt: "Kiehl's gift bags set out for guests" },
    { src: KIEHLS.bar, alt: "Live from the East Village bar at Kiehl's" },
    { src: KIEHLS.shelves, alt: "Kiehl's product wall" },
    { src: KIEHLS.backdrop, alt: "Guest with a Kiehl's gift in front of the branded backdrop" },
  ];

  return (
    <Box sx={{ border: `1px solid ${LINE}`, borderRadius: "20px", p: { xs: 2.5, md: 4 }, bgcolor: "rgba(255,255,255,0.03)" }}>
      <Typography sx={{ fontFamily: portalFont, color: ACCENT, fontWeight: 700, letterSpacing: "0.14em", fontSize: "0.75rem", mb: 1 }}>
        02
      </Typography>
      <Typography sx={{ fontFamily: portalFont, color: "#fff", fontWeight: 750, fontSize: { xs: "2.4rem", md: "3.25rem" }, letterSpacing: "-0.03em", lineHeight: 1.05, mb: 1.5 }}>
        Kiehl&apos;s
      </Typography>
      <Typography sx={{ fontFamily: portalFont, color: "#fff", fontSize: { xs: "1.15rem", md: "1.3rem" }, lineHeight: 1.5, maxWidth: 760, mb: 4 }}>
        An in-store takeover for a product launch.
      </Typography>
      <Typography sx={{ fontFamily: portalFont, color: "#fff", fontWeight: 750, fontSize: { xs: "1.35rem", md: "1.6rem" }, letterSpacing: "-0.02em", mb: 2 }}>
        The goals.
      </Typography>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 1.5, mb: 5 }}>
        {goals.map(([number, goal]) => (
          <Box key={number} sx={{ border: `1px solid ${LINE}`, borderRadius: "16px", px: 2.25, py: 2, bgcolor: "#000" }}>
            <Typography sx={{ fontFamily: portalFont, color: ACCENT, fontWeight: 700, letterSpacing: "0.12em", fontSize: "0.75rem", mb: 1 }}>
              {number}
            </Typography>
            <Typography sx={{ fontFamily: portalFont, color: "#fff", fontWeight: 650, fontSize: { xs: "1.15rem", md: "1.3rem" }, letterSpacing: "-0.02em", lineHeight: 1.35 }}>
              {goal}
            </Typography>
          </Box>
        ))}
      </Box>
      <Typography sx={{ fontFamily: portalFont, color: "#fff", fontWeight: 750, fontSize: { xs: "1.35rem", md: "1.6rem" }, letterSpacing: "-0.02em", mb: 2 }}>
        The store.
      </Typography>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(3, minmax(0, 1fr))", md: "repeat(3, 140px)" }, gap: { xs: 2, md: 3 }, justifyContent: "start", mb: 3 }}>
        <Talent name="Francis Ellis" src={KIEHLS.francis} />
        <Talent name="Gabby Bryan" src={KIEHLS.gabby} />
        <Talent name="Sydnee Washington" src={KIEHLS.sydnee} />
      </Box>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", md: "1fr 1fr 1fr 1fr" }, gap: 1.25 }}>
        {photos.map((photo) => (
          <Box key={photo.src} component="img" src={photo.src} alt={photo.alt} sx={{ width: "100%", height: { xs: 180, md: 240 }, objectFit: "cover", borderRadius: "12px", display: "block" }} />
        ))}
      </Box>
    </Box>
  );
}

const BRANDED_REELS = [
  { id: "katz", label: "Katz's", src: "https://www.instagram.com/reel/DezXO8wp6HP/embed" },
  { id: "luger", label: "Peter Luger's", src: "https://www.instagram.com/reel/Dd447Kape-M/embed" },
  { id: "paragon", label: "Paragon Sports", src: "https://www.instagram.com/reel/DYCrVx_JDzP/embed" },
] as const;

function BrandedExamples() {
  const [active, setActive] = useState<string | null>(null);
  const reel = BRANDED_REELS.find((item) => item.id === active);

  return (
    <Box sx={{ mt: { xs: 5, md: 6 }, pt: { xs: 3, md: 4 }, borderTop: `1px solid ${LINE}` }}>
      <Typography sx={{ fontFamily: portalFont, color: ACCENT, fontWeight: 700, letterSpacing: "0.14em", fontSize: "0.72rem", mb: 1 }}>
        MORE
      </Typography>
      <Typography sx={{ fontFamily: portalFont, color: "#fff", fontWeight: 750, fontSize: { xs: "1.6rem", md: "2rem" }, letterSpacing: "-0.03em", mb: 2 }}>
        Other branded content.
      </Typography>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.25 }}>
        {BRANDED_REELS.map((item) => {
          const selected = item.id === active;
          return (
            <Button
              key={item.id}
              onClick={() => setActive(selected ? null : item.id)}
              sx={{
                fontFamily: portalFont,
                fontWeight: 700,
                textTransform: "none",
                color: "#fff",
                border: "1px solid rgba(255,255,255,0.4)",
                borderRadius: "999px",
                px: 2,
                py: 0.8,
                bgcolor: selected ? "rgba(255,255,255,0.1)" : "transparent",
                "&:hover": { borderColor: "#fff", bgcolor: "rgba(255,255,255,0.06)" },
              }}
            >
              {item.label}
            </Button>
          );
        })}
      </Box>
      {reel ? (
        <Box sx={{ mt: 3, maxWidth: 420 }}>
          <Box
            component="iframe"
            key={reel.id}
            src={reel.src}
            title={`${reel.label} branded content on Instagram`}
            allow="autoplay; clipboard-write; encrypted-media; picture-in-picture"
            allowFullScreen
            sx={{ width: "100%", height: 680, border: 0, borderRadius: "16px", bgcolor: "#111" }}
          />
        </Box>
      ) : null}
    </Box>
  );
}

function Talent({ name, src }: { name: string; src: string }) {
  return (
    <Box sx={{ width: "100%", maxWidth: 140 }}>
      <Box sx={{ width: "100%", aspectRatio: "1", borderRadius: "50%", overflow: "hidden" }}>
        <Box component="img" src={src} alt={name} sx={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "center 18%", display: "block" }} />
      </Box>
      <Typography sx={{ fontFamily: portalFont, color: "#fff", fontWeight: 650, fontSize: "0.95rem", lineHeight: 1.3, mt: 1.25, textAlign: "center" }}>{name}</Typography>
    </Box>
  );
}

function Challenge({ title, body, src, alt }: { title: string; body: string; src: string; alt: string }) {
  return (
    <Box sx={{ border: `1px solid ${LINE}`, borderRadius: "16px", overflow: "hidden", bgcolor: "rgba(255,255,255,0.03)" }}>
      <Box component="img" src={src} alt={alt} sx={{ width: "100%", height: "auto", display: "block" }} />
      <Box sx={{ px: 2, py: 1.75 }}>
        <Typography sx={{ fontFamily: portalFont, color: "#fff", fontWeight: 750, fontSize: "1.05rem", letterSpacing: "-0.02em", mb: 0.5 }}>{title}</Typography>
        <Typography sx={{ fontFamily: portalFont, color: MUTED, fontSize: "0.95rem", lineHeight: 1.45 }}>{body}</Typography>
      </Box>
    </Box>
  );
}

function Q4Tab() {
  return (
    <Box sx={{ maxWidth: PORTAL_MAX_WIDTH, mx: "auto", px: { xs: 2.5, md: 4 }, py: { xs: 4, md: 6 } }}>
      <Typography sx={{ fontFamily: portalFont, color: ACCENT, fontWeight: 700, letterSpacing: "0.14em", fontSize: "0.75rem", mb: 1 }}>
        Q4
      </Typography>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1.1fr 0.9fr" }, gap: { xs: 3, md: 5 }, alignItems: "center" }}>
        <Box>
          <Typography sx={{ fontFamily: portalFont, color: "#fff", fontWeight: 750, fontSize: { xs: "2rem", md: "3rem" }, letterSpacing: "-0.03em", lineHeight: 1.05, mb: 2 }}>
            A budget-friendly taping.
          </Typography>
          <Typography sx={{ fontFamily: portalFont, color: "#8FDBA8", fontWeight: 750, fontSize: "1.8rem", mb: 2 }}>$15,000</Typography>
          <Typography sx={{ fontFamily: portalFont, color: "#fff", fontSize: "1.08rem", lineHeight: 1.6, mb: 2 }}>
            One 30-minute special with a weed-friendly comic. The target is Reg Thomas. We shoot it at a dispensary, or another room that is 420-friendly.
          </Typography>
          <Typography sx={{ fontFamily: portalFont, color: "#fff", fontSize: "1.08rem", lineHeight: 1.6 }}>
            It is a live show. Product sits in the background, and the clips carry a watermark. We use those clips and distribute the content for a month.
          </Typography>
        </Box>
        <Box
          component="img"
          src="https://storage.googleapis.com/cuo-website/Reg%20Thomas.jpg"
          alt="Reg Thomas"
          sx={{ width: "100%", maxHeight: 520, objectFit: "cover", borderRadius: "16px" }}
        />
      </Box>
    </Box>
  );
}

function TierCard({
  name,
  price,
  gets,
  provides,
  plus,
  badge,
}: {
  name: string;
  price?: string;
  gets: readonly string[];
  provides?: readonly string[];
  plus?: string;
  badge?: string;
}) {
  return (
    <Box sx={{ border: `1px solid ${LINE}`, borderRadius: "16px", p: { xs: 2.25, md: 2.75 }, bgcolor: "rgba(255,255,255,0.03)", color: "#fff" }}>
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1, mb: 0.5 }}>
        <Typography sx={{ fontFamily: portalFont, fontWeight: 800, letterSpacing: "0.08em", fontSize: "1.15rem", color: "#fff" }}>{name.toUpperCase()}</Typography>
        {badge ? (
          <Typography sx={{ fontFamily: portalFont, color: "#fff", fontSize: "0.68rem", fontWeight: 700, letterSpacing: "0.04em", lineHeight: 1.2, textAlign: "right", border: `1px solid ${LINE}`, borderRadius: "999px", px: 1, py: 0.45, flexShrink: 0 }}>
            {badge}
          </Typography>
        ) : null}
      </Box>
      {price ? (
        <Typography sx={{ fontFamily: portalFont, color: "#8FDBA8", fontWeight: 750, fontSize: "1.65rem", mt: 0.5, mb: 2 }}>{price}</Typography>
      ) : (
        <Box sx={{ mb: 2 }} />
      )}
      <Typography sx={{ fontFamily: portalFont, fontWeight: 700, mb: 1, color: "#fff" }}>You get:</Typography>
      {plus ? (
        <Typography sx={{ fontFamily: portalFont, color: "#fff", mb: 1 }}>{plus}</Typography>
      ) : null}
      <Box component="ul" sx={{ m: 0, pl: 2.25, mb: provides ? 2 : 0, color: "#fff" }}>
        {gets.map((item) => (
          <Typography key={item} component="li" sx={{ fontFamily: portalFont, color: "#fff", lineHeight: 1.45, mb: 0.6 }}>
            {item}
          </Typography>
        ))}
      </Box>
      {provides ? (
        <>
          <Typography sx={{ fontFamily: portalFont, fontWeight: 700, mb: 1, color: "#fff" }}>You provide:</Typography>
          <Box component="ul" sx={{ m: 0, pl: 2.25, color: "#fff" }}>
            {provides.map((item) => (
              <Typography key={item} component="li" sx={{ fontFamily: portalFont, color: "#fff", lineHeight: 1.45, mb: 0.6 }}>
                {item}
              </Typography>
            ))}
          </Box>
        </>
      ) : null}
    </Box>
  );
}
